/* global debounce */

if (!customElements.get('search-form')) {
  class SearchForm extends HTMLElement {
    constructor() {
      super();
      this.input = this.querySelector('.js-search-input');
      this.productTypeSelect = this.querySelector('.js-search-product-types');
      this.productTypeInput = document.getElementById('product_type_input');
      this.resetBtn = this.querySelector('.js-search-reset');
      this.form = this.closest('form');
      this.restoreDisplayTerm();
      this.addListeners();
      this.init();
    }

    addListeners() {
      this.input.addEventListener('input', debounce(this.handleInput.bind(this)));
      if (this.productTypeSelect) {
        this.productTypeSelect.addEventListener('change', this.handleProductTypeChange.bind(this));
      }
      this.resetBtn.addEventListener('click', this.handleReset.bind(this));
      if (this.form) {
        // Runs after predictive-search's own submit handler (registered earlier), so
        // evt.defaultPrevented already reflects its decision.
        this.form.addEventListener('submit', this.handleScopeSubmit.bind(this));
      }
    }

    init() {
      if ((this.input.dataset.placeholderTwo || this.input.dataset.placeholderThree)
        && (this.input.dataset.placeholderPromptsMob === 'true' || theme.mediaMatches.md)) {
        this.typeInPlaceholders();
      }
    }

    /**
     * Simulates the placeholder titles being typed in and the previous one deleted, on repeat
     */
    typeInPlaceholders() {
      const typingSpeed = this.input.dataset.typingSpeed || 100;
      const deletingSpeed = this.input.dataset.deletingSpeed || 60;
      const delayAfterDeleting = this.input.dataset.delayAfterDeleting || 500;
      const delayBeforeFirstDelete = this.input.dataset.delayBeforeFirstDelete || 2000;
      const delayAfterWordTyped = this.input.dataset.delayAfterWordTyped || 2400;
      const placeholders = [];

      if (this.input.placeholder) {
        placeholders.push(this.input.placeholder);
      }

      if (this.input.dataset.placeholderTwo) {
        placeholders.push(this.input.dataset.placeholderTwo);
      }

      if (this.input.dataset.placeholderThree) {
        placeholders.push(this.input.dataset.placeholderThree);
      }

      const typeInNextPlaceholder = async (placeholder) => {
        await new Promise((resolve) => {
          let currentPlaceholder = this.input.getAttribute('placeholder');

          // If necessary remove the start of the word to type in if it already exists in the
          // placeholder
          let nextPlaceholder = (currentPlaceholder.length >= 3
              && placeholder.startsWith(currentPlaceholder))
            ? placeholder.replace(currentPlaceholder, '')
            : placeholder;

          const typingIntervalId = setInterval(() => {
            currentPlaceholder = this.input.getAttribute('placeholder');
            this.input.setAttribute('placeholder', currentPlaceholder + nextPlaceholder.charAt(0));
            if (nextPlaceholder.length === 1) {
              resolve();
              clearInterval(typingIntervalId); // Stop typing
            } else {
              nextPlaceholder = nextPlaceholder.substring(1);
            }
          }, typingSpeed);
        });
      };

      const deleteCurrentPlaceholder = async (nextPlaceholder) => {
        await new Promise((resolve) => {
          let prevPlaceholder = this.input.getAttribute('placeholder');
          const deletionIntervalId = setInterval(() => {
            const newPlaceholder = prevPlaceholder.substring(0, prevPlaceholder.length - 1);
            this.input.setAttribute('placeholder', newPlaceholder);
            prevPlaceholder = newPlaceholder;
            if (prevPlaceholder.length === 0
              || (prevPlaceholder.length >= 3 && nextPlaceholder.startsWith(prevPlaceholder))) {
              resolve();
              clearInterval(deletionIntervalId);
            }
          }, deletingSpeed);
        });
      };

      let startIndex = 0;
      const showNextPlaceholder = () => {
        startIndex = (startIndex + 1) % placeholders.length;
        const nextPlaceholder = placeholders[startIndex];
        deleteCurrentPlaceholder.call(this, nextPlaceholder).then(() => {
          setTimeout(() => {
            typeInNextPlaceholder.call(this, nextPlaceholder).then(() => {
              setTimeout(showNextPlaceholder.bind(this), delayAfterWordTyped);
            });
          }, delayAfterDeleting);
        });
      };

      setTimeout(showNextPlaceholder.bind(this), delayBeforeFirstDelete);
    }

    /**
     * Handles 'input' events on the search field.
     */
    handleInput() {
      if (this.input.value.length > 0) {
        this.input.classList.add('search__input--dirty');
      } else {
        this.input.classList.remove('search__input--dirty');
      }
    }

    /**
     * Handles a change of product type
     * @param {object} evt - Event object.
     */
    handleProductTypeChange(evt) {
      this.productTypeInput.value = evt.detail.selectedValue;
    }

    /**
     * Handles the reset button being clicked
     */
    handleReset() {
      this.input.classList.remove('search__input--dirty');
    }

    /**
     * Rewrites the query on submit so /search matches only structured product fields
     * (title, tag, product_type, variant title, vendor) and never the product body.
     * This stops fitment tables / shared "About" blocks in descriptions from surfacing
     * products of unrelated motorcycle models. Mirrors the fields the predictive
     * (dropdown) search already scopes to. Applied globally to every search form.
     * @param {object} evt - Submit event.
     */
    handleScopeSubmit(evt) {
      // predictive-search may have cancelled the submit (empty query, or a keyboard-
      // selected suggestion navigating elsewhere) — leave those untouched.
      if (evt.defaultPrevented) return;
      const raw = this.input.value.trim();
      if (!raw || SearchForm.isScopedQuery(raw)) return;
      const scoped = SearchForm.buildScopedQuery(raw);
      if (scoped) this.input.value = scoped;
    }

    /**
     * Builds a field-scoped, body-excluding query from raw user terms.
     * Each whitespace token becomes an OR across product fields with a trailing `*`
     * so partial/prefix matching (e.g. "hayab" -> Hayabusa) is preserved. Tokens are
     * ANDed together, matching Shopify's default multi-term behaviour.
     * @param {string} raw - Raw search terms typed by the user.
     * @returns {string} Scoped query, or '' when nothing usable remains.
     */
    static buildScopedQuery(raw) {
      const fields = ['title', 'tag', 'product_type', 'variants.title', 'vendor'];
      const tokens = raw
        .split(/\s+/)
        // Strip characters that would break the search grammar or inject field syntax.
        .map((token) => token.replace(/["():*]/g, '').replace(/^-+/, '').trim())
        .filter(Boolean);
      if (!tokens.length) return '';
      return tokens
        .map((token) => `(${fields.map((field) => `${field}:${token}*`).join(' OR ')})`)
        .join(' AND ');
    }

    /**
     * True when the value already looks like a query this component generated,
     * so we neither re-scope it nor show its raw syntax to the user.
     * @param {string} value - Query value to test.
     * @returns {boolean}
     */
    static isScopedQuery(value) {
      return /\(title:\S+\*/.test(value) && value.includes('tag:') && value.includes('product_type:');
    }

    /**
     * Recovers the human-readable terms from a scoped query (each group starts with
     * `(title:TOKEN*`), for display purposes.
     * @param {string} scoped - Scoped query value.
     * @returns {string} Space-joined original tokens.
     */
    static extractDisplayTerm(scoped) {
      const matches = [...scoped.matchAll(/\(title:([^\s)]+?)\*/g)];
      return matches.map((match) => match[1]).join(' ');
    }

    /**
     * On the results page the persisted `q` holds our scoped syntax; swap the visible
     * input (and any "no results" message) back to the clean terms the user typed.
     */
    restoreDisplayTerm() {
      if (!this.input || !SearchForm.isScopedQuery(this.input.value)) return;
      const scoped = this.input.value;
      const term = SearchForm.extractDisplayTerm(scoped);
      if (!term) return;
      this.input.value = term;
      const noResults = document.querySelector('[data-search-no-results]');
      if (noResults && noResults.textContent.indexOf(scoped) !== -1) {
        noResults.textContent = noResults.textContent.replace(scoped, term);
      }
    }
  }

  customElements.define('search-form', SearchForm);
}
